import React, { useEffect, useRef, useState } from 'react';
import { 
  FaGraduationCap, FaClock, FaUsers, FaArrowRight, 
  FaStar, FaArrowLeft, FaCertificate, FaRocket, 
  FaBriefcase, FaUniversity, FaMapMarkerAlt, FaPhone
} from 'react-icons/fa';
import { 
  MdArtTrack, MdBusinessCenter, MdComputer
} from 'react-icons/md';
import { 
  GiBrain, GiBookshelf, GiMicroscope
} from 'react-icons/gi';

const CoursesSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      checkScrollButtons();
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const cardWidth = 380; // Width of each card
      const gap = 24; // Gap between cards
      const scrollAmount = direction === 'left' ? -(cardWidth + gap) : (cardWidth + gap);
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const courses = [
    {
      name: "Bachelor of Arts",
      shortName: "B.A.",
      icon: MdArtTrack,
      duration: "3 Years",
      seats: 180,
      fee: "₹12,000/year",
      description: "Comprehensive arts education with specializations in Humanities, Social Sciences, and Languages.",
      career: "Teaching, Civil Services, Journalism, Social Work, Research",
      rating: 4.8,
      features: [
        "हिन्दी (Hindi)", "समाजशास्त्र (Sociology)", "शिक्षा शास्त्र (Education)",
        "गृह विज्ञान (Home Science)", "अंग्रेजी (English)", "अर्थशास्त्र (Economics)",
        "भूगोल (Geography)", "प्राचीन इतिहास (Ancient History)",
        "राजनीति शास्त्र (Political Science)", "संस्कृत (Sanskrit)"
      ]
    },
    {
      name: "Bachelor of Science",
      shortName: "B.Sc.",
      icon: GiMicroscope,
      duration: "3 Years",
      seats: 120,
      fee: "₹15,000/year",
      description: "Advanced science education with state-of-the-art laboratories and focus on practical learning.",
      career: "Research Scientist, Data Analyst, Teaching, Environmental Science",
      rating: 4.9,
      features: [
        "भौतिक विज्ञान (Physics)", "रसायन विज्ञान (Chemistry)",
        "वनस्पति विज्ञान (Botany)", "प्राणि विज्ञान (Zoology)", "गणित (Mathematics)"
      ]
    },
    {
      name: "Bachelor of Commerce",
      shortName: "B.Com.",
      icon: MdBusinessCenter,
      duration: "3 Years",
      seats: 80,
      fee: "₹16,000/year",
      description: "Professional commerce education with focus on accounting, finance, and business management.",
      career: "CA, Financial Analyst, Banking, Entrepreneurship",
      rating: 4.7,
      features: ["Accounting", "Finance", "Economics", "Business Law", "Taxation"]
    },
    {
      name: "Bachelor of Business Administration",
      shortName: "BBA",
      icon: MdBusinessCenter,
      duration: "3 Years",
      seats: 60,
      fee: "₹20,000/year",
      description: "Professional management program focusing on business principles, leadership, and entrepreneurial skills.",
      career: "Business Analyst, Marketing Manager, Entrepreneur, HR Executive",
      rating: 4.8,
      features: ["Marketing", "Finance", "Human Resource", "Business Analytics"]
    },
    {
      name: "Bachelor of Computer Applications",
      shortName: "BCA",
      icon: MdComputer,
      duration: "3 Years",
      seats: 90,
      fee: "₹25,000/year",
      description: "Industry-focused IT education with cutting-edge curriculum and practical training.",
      career: "Software Developer, Web Designer, System Analyst, IT Consultant",
      rating: 4.9,
      features: ["Programming", "Web Development", "Database", "Cloud Computing"]
    },
    {
      name: "Master of Arts",
      shortName: "M.A.",
      icon: GiBookshelf,
      duration: "2 Years",
      seats: 90,
      fee: "₹18,000/year",
      description: "Advanced postgraduate program for in-depth knowledge and research in selected humanities disciplines.",
      career: "Professor, Researcher, Civil Services, Content Strategist",
      rating: 4.8,
      features: [
        "हिन्दी (Hindi)", "गृह विज्ञान (Home Science)",
        "समाजशास्त्र (Sociology)", "प्राचीन इतिहास (Ancient History)"
      ]
    },
    {
      name: "Master of Science",
      shortName: "M.Sc.",
      icon: GiMicroscope,
      duration: "2 Years",
      seats: 60,
      fee: "₹20,000/year",
      description: "Advanced research-oriented science program with specialization options in Physics, Chemistry, Mathematics, and Zoology.",
      career: "Research Scientist, Academician, Data Analyst, Environmental Consultant",
      rating: 4.7,
      features: ["Physics", "Chemistry", "Mathematics", "Zoology", "Research Methodology"]
    },
    {
      name: "Diploma in Elementary Education",
      shortName: "D.El.Ed",
      icon: FaGraduationCap,
      duration: "2 Years",
      seats: 100,
      fee: "₹8,000/year",
      description: "Teacher training program for aspiring elementary school teachers, focusing on pedagogy and child development.",
      career: "Primary School Teacher, Curriculum Developer, Educational Consultant",
      rating: 4.5,
      features: ["Child Psychology", "Pedagogy", "Curriculum Design", "Classroom Management"]
    }
  ];

  // Placeholder data for affiliated colleges – replace with actual data
  const colleges = [
    {
      name: "Mahatma Gandhi College",
      location: "Jaunpur, UP",
      phone: "+91-1234567890",
      coursesOffered: ["BA", "BSc", "BCom", "BBA", "BCA", "MA", "MSc", "DElEd"]
    },
    {
      name: "Veer Bahadur Singh College",
      location: "Jaunpur, UP",
      phone: "+91-9876543210",
      coursesOffered: ["BA", "BSc", "BCom", "MA", "MSc"]
    },
    {
      name: "Shri Ram College of Education",
      location: "Jaunpur, UP",
      phone: "+91-9988776655",
      coursesOffered: ["BA", "BSc", "DElEd"]
    },
    {
      name: "Kasturba Gandhi College",
      location: "Jaunpur, UP",
      phone: "+91-8899776655",
      coursesOffered: ["BA", "BSc", "BCom", "BCA"]
    },
    {
      name: "Rajiv Gandhi College of Science",
      location: "Jaunpur, UP",
      phone: "+91-7788996655",
      coursesOffered: ["BSc", "MSc", "BCA"]
    },
    {
      name: "Jawahar Lal Nehru College",
      location: "Jaunpur, UP",
      phone: "+91-6677889900",
      coursesOffered: ["BA", "BCom", "BBA", "MA"]
    }
  ];

  return (
    <section ref={sectionRef} className="w-full py-20 bg-gradient-to-b from-red-50 via-blue-50/20 to-white overflow-hidden">
      <div className="w-full px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center max-w-4xl mx-auto mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-3 bg-blue-600 px-6 py-3 rounded-full mb-5">
            <FaGraduationCap className="text-blue-600 text-base" />
            <span className="text-white-700 font-semibold text-base">Academic Programs</span>
            <FaStar className="text-blue-600 text-sm" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Academic Courses
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-blue-600 to-blue-700 mx-auto rounded-full mb-4"></div>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
            Affiliated to <strong>Veer Bahadur Singh Purvanchal University, Jaunpur</strong> (Code: 686)
          </p>
        </div>

        {/* Scrollable Cards Container */}
        <div className="relative">
          {/* Left Scroll Button */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300 border border-gray-200"
            >
              <FaArrowLeft className="text-sm" />
            </button>
          )}

          {/* Right Scroll Button */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300 border border-gray-200"
            >
              <FaArrowRight className="text-sm" />
            </button>
          )}

          {/* Horizontal Scroll Container */}
          <div
            ref={scrollContainerRef}
            className="flex overflow-x-auto gap-6 pb-6 scrollbar-hide scroll-smooth"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {courses.map((course, idx) => (
              <div
                key={idx}
                className="flex-none w-[340px] md:w-[380px] bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer group overflow-hidden"
              >
                {/* Top Gradient Bar */}
                <div className="h-1.5 w-full bg-gradient-to-r from-blue-600 to-blue-700"></div>
                
                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                      <course.icon className="text-white text-2xl" />
                    </div>
                    <div className="flex items-center gap-1 bg-blue-50 px-2.5 py-1 rounded-full">
                      <FaStar className="text-blue-600 text-xs" />
                      <span className="text-xs font-bold text-gray-700">{course.rating}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-blue-600 transition-colors">
                    {course.name}
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">{course.shortName}</p>

                  {/* Description */}
                  <p className="text-gray-600 text-xs leading-relaxed mb-3 line-clamp-2">
                    {course.description}
                  </p>

                  {/* Features Tags (only show first 3, with a "+X" for more) */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {course.features.slice(0, 3).map((feature, i) => (
                      <span key={i} className="text-[10px] bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                        {feature}
                      </span>
                    ))}
                    {course.features.length > 3 && (
                      <span className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        +{course.features.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-3 gap-2 mb-3 pb-3 border-b border-gray-100">
                    <div className="text-center">
                      <FaClock className="text-blue-600 mx-auto text-xs mb-1" />
                      <p className="text-[10px] text-gray-500">Duration</p>
                      <p className="text-xs font-semibold text-gray-700">{course.duration}</p>
                    </div>
                    <div className="text-center">
                      <FaUsers className="text-blue-600 mx-auto text-xs mb-1" />
                      <p className="text-[10px] text-gray-500">Seats</p>
                      <p className="text-xs font-semibold text-gray-700">{course.seats}</p>
                    </div>
                    <div className="text-center">
                      <FaCertificate className="text-blue-600 mx-auto text-xs mb-1" />
                      <p className="text-[10px] text-gray-500">Fee/Year</p>
                      <p className="text-xs font-semibold text-gray-700">{course.fee}</p>
                    </div>
                  </div>

                  {/* Career */}
                  <div className="mb-3">
                    <div className="flex items-center gap-1 mb-1">
                      <FaBriefcase className="text-blue-600 text-[10px]" />
                      <p className="text-[10px] font-semibold text-gray-600">Career Paths</p>
                    </div>
                    <p className="text-[10px] text-gray-500 line-clamp-1">{course.career}</p>
                  </div>

                  {/* Button */}
                  <button className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md hover:shadow-lg">
                    <span>Apply Now</span>
                    <FaArrowRight className="text-xs group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`text-center mt-10 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <button className="group px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-full font-semibold text-base hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto">
            <FaRocket className="text-lg" />
            View All Programs
            <FaArrowRight className="group-hover:translate-x-1 transition-transform text-base" />
          </button>
        </div>
      </div>

      {/* Hide scrollbar styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default CoursesSection;